import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Box, Typography } from '@mui/material';
import { ERouteLinks } from 'models/route';
import { ESearchFilterKeys, ESearchFilterTypes } from 'constants/SearchFilterTypes';
import { useFilterPresets } from 'hooks/useFilterPresets';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import {
  IFormElementOptionProp,
  initialAncillariesSearchFormValues,
  ISearchAncillariesFormValues,
} from 'models/form';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { getFieldsByComponentType, isEmptyObject } from 'pages/AncillariesPage/helpers';
import {
  EAncillaryFilterTypes,
  EAncillaryState,
  EArtAncillaryExpandType,
  EArtAncillaryType,
  EArtAncillaryTypeNameLowerCase,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { formatDateTimeToIso } from 'utils/date';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { EFirearmType } from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTComponentsForm/helpers';
import { ESearchFormFirearmLabels } from 'pages/Firearms/RegisteredFirearmsSearchForm';
import { getSearchFilter } from 'requests/searchFilter';
import { ISearchParams } from 'pages/UsersPage';
import { isEmpty } from 'utils/search';
import { IAncillary } from 'models/ancillaries';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { EPerPages, ESortDirectionTypes } from 'models/table';
import {
  StyledAdvancedSearch,
  StyledFormSelect,
  StyledPresetSelect,
  StyledSubtitle,
} from './styles';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { FormAsyncMultiAutocomplete } from 'components/molecules/FormAsyncMultiAutocomplete';
import { multiFieldsList } from 'helpers/multiFieldsList';
import { getFirearmTypesForComponent } from 'utils/ancillaryComponentSearchUtils';

export enum ESearchFormLabels {
  ancillaryTypeUniqueId = 'componentsTable.accordion.componentType.label',
  fireCycleId = 'componentsTable.accordion.fireCycleId.label',
  marking = 'ancillariesTable.accordion.marking.label',
  serialNumber = 'componentsTable.accordion.serialNumber.label',
  exhibitNumber = 'componentsTable.accordion.exhibitNumber.label',
  manufacturersUniqueIds = 'componentsTable.accordion.componentManufacturer.label',
  productName = 'componentsTable.accordion.productName.label',
  calibresUniqueIds = 'componentsTable.accordion.calibre.label',
  length = 'componentsTable.accordion.length.label',
  compositionsUniqueIds = 'componentsTable.accordion.composition.label',
  componentCapacitiesUniqueIds = 'componentsTable.accordion.capacity.label',
  proofHousesUniqueIds = 'componentsTable.accordion.proofhouse.label',
  statesUniqueIds = 'componentsTable.accordion.componentState.label',
  legalityUniqueId = 'componentsTable.accordion.componentLegality.label',
  frtTypesUniqueIds = 'componentsTable.accordion.frtType.label',
  frtMakesUniqueIds = 'componentsTable.accordion.frtMake.label',
  frtModelsUniqueIds = 'componentsTable.accordion.frtModel.label',
  frtManufacturersUniqueIds = 'componentsTable.accordion.frtManufacturer.label',
  owner = 'accessoriesTable.accordion.owner.label',
  keeper = 'accessoriesTable.accordion.keeper.label',
  ownerUniqueId = 'componentsTable.accordion.owner.label',
  keeperUniqueId = 'componentsTable.accordion.keeper.label',
}

export enum ESelectLE {
  keeper = 'keeper',
  owner = 'owner',
}

interface ISearchForm {
  onSubmit: (arg: any) => void;
  ownerOptions: IFormElementOptionProp[];
  setOwnerOptions: (opt: IFormElementOptionProp[]) => void;
  keeperOptions: IFormElementOptionProp[];
  setKeeperOptions: (opt: IFormElementOptionProp[]) => void;
  showAdvanced?: boolean;
  componentsUniqueIds?: string[];
  isFromMap?: boolean;
  returnPath: string;
  getNewQueryPath?: (args: any) => string;
  setShowAdvanced?: (val: boolean) => void;
  initialFormValues?: ISearchAncillariesFormValues;
  customFields?: ICustomFieldByObjectType[];
  searchAncillaryFromLE?: boolean;
  isSearchButtonDisabled?: boolean;
  addAncillaryToGroupContext?: boolean;
  selectedAncillaries?: IAncillary[];
  currentPage?: number;
  perPage?: EPerPages;
}

const customFieldWrapper = (children: React.ReactNode) => (
  <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>{children}</Grid>
);

const ComponentsSearchForm = ({
  ownerOptions,
  keeperOptions,
  setOwnerOptions,
  setKeeperOptions,
  showAdvanced = true,
  setShowAdvanced = () => {},
  componentsUniqueIds,
  isFromMap = false,
  returnPath,
  onSubmit,
  getNewQueryPath,
  initialFormValues = initialAncillariesSearchFormValues,
  searchAncillaryFromLE = false,
  isSearchButtonDisabled = false,
  addAncillaryToGroupContext = false,
  selectedAncillaries,
  customFields = [],
  perPage,
  currentPage,
}: ISearchForm) => {
  const [values, setValues] = useState<ISearchAncillariesFormValues>({
    ...initialFormValues,
    componentsUniqueIds,
  });
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const { state: routerState }: any = useLocation();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ISearchAncillariesFormValues>({
    defaultValues: initialFormValues,
  });

  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const [searchParams] = useSearchParams();
  const presetId =
    searchParams.get('presetId') ||
    searchParams.get('preset') ||
    routerState?.presetAncillaries;
  const expandParam = searchParams.get('expand');
  const watchPreset = watch('preset');
  const watchComponentTypeUniqueId = watch('ancillaryTypeUniqueId');
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [routerFormValues] = useState({
    ...routerState?.formValues,
    ...routerState?.componentFormValues,
  });
  const [routerOptions] = useState({
    ...routerState?.options,
    ...routerState?.componentOptions,
  });

  const {
    loadDictionaries,
    legality,
    state,
    artComponentCalibre,
    artComponentCapacity,
    artComposition,
    artComponentManufacturer,
    artComponentType,
    artComponentProofHouse,
    make,
    model,
    manufacturer,
    firearmType,
  } = useAncillaries();

  const { getFilterPresets, presets, getPresetValues, preparePresetsList } =
    useFilterPresets();

  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);

  useEffect(() => {
    if (!isInitialFormLoad) {
      handleSubmit((val) => onSubmit({ ...val, invokedByPageChange: true }))();
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  const setPresetFilters = async (
    selectedPreset: string,
    isFromEmailLink: boolean,
    isClickedManually: boolean = false,
    isInitialLoad: boolean = false,
  ) => {
    const handleValues = async () => {
      if (isFromEmailLink) {
        const filter = await getSearchFilter(selectedPreset);
        if (filter) {
          const queryValues: ISearchParams = {};
          filter.values.forEach(({ key, value }) => {
            if (multiFieldsList.includes(key)) {
              if (!Array.isArray(queryValues[key])) {
                queryValues[key] = [];
              }
              (queryValues[key] as string[]).push(value);
            } else {
              queryValues[key] = value;
            }
          });
          return queryValues;
        }
      }
      return getPresetValues(selectedPreset);
    };

    const initialValues =
      isInitialLoad && !routerState?.fromSelectLE
        ? { ...initialFormValues, ...routerFormValues }
        : initialFormValues;

    const presetValues: any = {
      ...(isClickedManually ? initialAncillariesSearchFormValues : initialValues),
      preset: selectedPreset,
      ...(await handleValues()),
    };

    if (isClickedManually) {
      customFields
        .filter((it) => it.searchCriteria)
        .forEach((it) => {
          if (it.customFieldType === ECustomFieldType.DATE) {
            presetValues[`from_${it.name}`] = '';
            presetValues[`to_${it.name}`] = '';
          } else {
            presetValues[it.name] = '';
          }
        });
    }

    if (presetValues.ownerUniqueId && presetValues.ownerName) {
      setOwnerOptions([
        {
          label: presetValues.ownerName,
          value: presetValues.ownerUniqueId,
        },
      ]);
    }

    if (presetValues.keeperUniqueId && presetValues.keeperName) {
      setKeeperOptions([
        {
          label: presetValues.keeperName,
          value: presetValues.keeperUniqueId,
        },
      ]);
    }

    if (presetValues.ancillaryTypeUniqueId) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      componentTypeChange(presetValues.ancillaryTypeUniqueId);
    }
    setValues(presetValues);
    if (selectedPreset !== '') {
      setShowAdvanced(true);
    }
    reset(presetValues);

    if (isClickedManually) {
      await handleSubmit(onSubmit)();
    }
  };

  const setLegalEntity = () => {
    const formValues = {
      ...initialFormValues,
      ...routerFormValues,
    };

    if (routerState?.type && routerState?.legalEntity) {
      const legalName = routerState?.legalEntity?.organizationName
        ? routerState?.legalEntity?.organizationName
        : `${routerState?.legalEntity?.firstName} ${routerState?.legalEntity?.lastName}`;
      const option = [
        {
          label: legalName,
          value: routerState.legalEntity.id,
        },
      ];
      if (routerState.type === ESelectLE.keeper) {
        setKeeperOptions(option);
        formValues.keeperName = legalName;
        if (routerOptions?.ownerOptions) {
          setOwnerOptions(routerOptions.ownerOptions);
        }
      } else {
        setOwnerOptions(option);
        formValues.ownerName = legalName;
        if (routerOptions?.keeperOptions) {
          setKeeperOptions(routerOptions.keeperOptions);
        }
      }
      formValues[routerState?.type === ESelectLE.owner ? 'ownerUniqueId' : 'keeperUniqueId'] =
        routerState.legalEntity.id;
    }
    setValues(formValues);
    setShowAdvanced(true);
    reset(formValues);
  };

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchAncillariesFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.Legality,
        EDictionaryTypes.State,
        EDictionaryTypes.ArtComponentCalibre,
        EDictionaryTypes.ArtComponentManufacturer,
        EDictionaryTypes.ArtComponentType,
        EDictionaryTypes.ArtComponentProofHouse,
        EDictionaryTypes.ManufacturerCountry,
        EDictionaryTypes.ArtComposition,
        EDictionaryTypes.ArtComponentCapacity,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.FirearmType,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (
      routerFormValues &&
      routerState?.module === EArtAncillaryTypeNameLowerCase.COMPONENTS
    ) {
      const formValues = {
        ...routerFormValues,
        legalityUniqueId: routerState?.legalityUniqueId,
        keeperUniqueId: routerOptions?.keeperOptions?.[0]?.value || '',
        ownerUniqueId: routerOptions?.ownerOptions?.[0]?.value || '',
      };
      setValues(formValues);
      reset(formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isEmpty(values.ancillaryTypeUniqueId) &&
      isEmpty(values.fireCycleId) &&
      isEmpty(values.marking) &&
      isEmpty(values.serialNumber) &&
      isEmpty(values.exhibitNumber) &&
      !values.manufacturersUniqueIds?.length &&
      isEmpty(values.productName) &&
      !values.calibresUniqueIds?.length &&
      isEmpty(values.length) &&
      !values.compositionsUniqueIds?.length &&
      !values.componentCapacitiesUniqueIds?.length &&
      !values.proofHousesUniqueIds?.length &&
      !values.statesUniqueIds?.length &&
      isEmpty(values.legalityUniqueId) &&
      isEmpty(values.ownerUniqueId) &&
      isEmpty(values.keeperUniqueId) &&
      !values.frtTypesUniqueIds?.length &&
      !values.frtMakesUniqueIds?.length &&
      !values.frtModelsUniqueIds?.length &&
      !values.frtManufacturersUniqueIds?.length
    ) {
      setShowSaveFilter(false);
    } else {
      setShowSaveFilter(true);
    }
  }, [values]);

  useEffect(() => {
    (async function init() {
      await getFilterPresets(ESearchFilterTypes.ancillaries, EArtAncillaryType.COMPONENTS);

      if (routerOptions?.keeperOptions?.length) {
        setKeeperOptions(routerOptions.keeperOptions);
      }

      if (routerOptions?.ownerOptions?.length) {
        setOwnerOptions(routerOptions.ownerOptions);
      }

      if (presetId && expandParam === EArtAncillaryExpandType.COMPONENTS) {
        await setPresetFilters(presetId, true);
        await handleSubmit(onSubmit)();
      }

      if (
        routerState?.presetAncillaries &&
        routerState?.ancillaryFilterType === EAncillaryFilterTypes.COMPONENT
      ) {
        setValue('preset', routerState.presetAncillaries);
      }

      if (!isEmptyObject(routerFormValues)) {
        setShowAdvanced(true);
      }
      if (routerState?.legalEntity) {
        setLegalEntity();
      }
      if (
        routerState?.module === EArtAncillaryTypeNameLowerCase.ACCESSORY ||
        routerState?.module === EArtAncillaryTypeNameLowerCase.AMMUNITION
      ) {
        reset(values);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      Object.values(EArtComponentType).includes(
        watchComponentTypeUniqueId as EArtComponentType,
      )
    ) {
      const fields = getFieldsByComponentType(watchComponentTypeUniqueId as EArtComponentType);
      setAccessFields(fields);
    }
    if (watchComponentTypeUniqueId === '') {
      setAccessFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchComponentTypeUniqueId]);

  const prepareData = (data: ISearchAncillariesFormValues): ISearchAncillariesFormValues => {
    const customFieldsPayload: ISearchFormCustomFieldValue[] = [];

    Object.keys(data).map((key) => {
      const isDateCF = key.startsWith('from_') || key.startsWith('to_');

      if (!isDateCF) {
        const customField = customFields.find((item) => item.name === key);
        if (customField && data[key]) {
          const customFieldItem: ISearchFormCustomFieldValue = {
            customFieldUniqueId: customField.uniqueId,
            customFieldType: customField.customFieldType,
            name: customField.name,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (data[key] === undefined || data[key] === null) {
              customFieldItem.text = undefined;
            } else if (data[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = data[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            customFieldItem.bool = data[key] === '1';
          }

          customFieldsPayload.push(customFieldItem);
          delete data[key];
        }
      } else {
        const customFieldKey = key.startsWith('from_')
          ? key.replace('from_', '')
          : key.replace('to_', '');

        const customField = customFields.find((item) => item.name === customFieldKey);

        if (customField && (data[`from_${customFieldKey}`] || data[`to_${customFieldKey}`])) {
          const customFieldItem: ISearchFormCustomFieldValue = {
            customFieldUniqueId: customField.uniqueId,
            customFieldType: customField.customFieldType,
            name: customField.name,
          };
          if (data[`from_${customFieldKey}`]) {
            customFieldItem.dateFrom =
              data[`from_${customFieldKey}`] !== null
                ? formatDateTimeToIso(data[`from_${customFieldKey}`])
                : formatDateTimeToIso(new Date().toString());
          }
          if (data[`to_${customFieldKey}`]) {
            customFieldItem.dateTo =
              data[`to_${customFieldKey}`] !== null
                ? formatDateTimeToIso(data[`to_${customFieldKey}`])
                : formatDateTimeToIso(new Date().toString());
          }
          customFieldsPayload.push(customFieldItem);
          if (data[`from_${customFieldKey}`]) {
            delete data[`from_${customFieldKey}`];
          }
          if (data[`to_${customFieldKey}`]) {
            delete data[`to_${customFieldKey}`];
          }
        }
      }
      return false;
    });

    if (customFieldsPayload?.length) {
      data.customFields = customFieldsPayload;
    } else {
      delete data.customFields;
    }

    if (ownerOptions.length > 0) {
      data.ownerName = ownerOptions[0]?.label || '';
    }
    if (keeperOptions.length > 0) {
      data.keeperName = keeperOptions[0]?.label || '';
    }
    return data;
  };

  const submitForm = (data: ISearchAncillariesFormValues) => {
    onSubmit({ ...prepareData(data), preset: undefined, formSubmitted: true });
  };

  const componentTypeChange = (value: any) => {
    const currentValues = getValues();
    const formValues = {
      ...initialAncillariesSearchFormValues,
      ancillaryTypeUniqueId: value,
      preset: currentValues.preset,
      ownerUniqueId: currentValues.ownerUniqueId,
      keeperUniqueId: currentValues.keeperUniqueId,
    };

    setValues(formValues);

    if (value) {
      const fields = getFieldsByComponentType(value);
      setAccessFields(fields);
    } else {
      setAccessFields([]);
    }
  };

  useEffect(() => {
    if (initialFormValues?.ancillaryTypeUniqueId) {
      const fields = getFieldsByComponentType(
        initialFormValues.ancillaryTypeUniqueId as EArtComponentType,
      );
      setAccessFields(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetValues = () => {
    const formValues = { ...getValues() };
    Object.keys(formValues).map((key: string) => {
      if (typeof values[key] === 'string') {
        values[key] = '';
      } else if (typeof values[key] === 'object') {
        values[key] = null;
      }
      return false;
    });
    reset(values);
  };

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    const currentValues = getValues();
    if (name) {
      if (name === 'ancillaryTypeUniqueId') {
        setValue('ancillaryTypeUniqueId', '');
        resetValues();
        componentTypeChange(null);
        onSubmit(values);
        return;
      }
      if (name === 'ownerUniqueId') {
        setOwnerOptions([]);
        currentValues.ownerUniqueId = '';
      }
      if (name === 'keeperUniqueId') {
        setKeeperOptions([]);
        currentValues.keeperUniqueId = '';
      }
      reset({ ...currentValues, [name]: '' });
    } else {
      resetValues();
    }

    if (refreshResults) {
      onSubmit(values);
    }
  };

  useEffect(() => {
    (async function init() {
      if (watchPreset) {
        await setPresetFilters(watchPreset, false, false, true);
      } else {
        await setPresetFilters('', false, false, true);
      }
      await handleSubmit(onSubmit)();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialAncillariesSearchFormValues.ancillaryTypeUniqueId) {
      componentTypeChange(initialAncillariesSearchFormValues.ancillaryTypeUniqueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAncillariesSearchFormValues]);

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  const clearAfterComponentTypeChange = () => {
    const formValues = { ...getValues() };
    Object.keys(formValues).map((key: string) => {
      if (typeof values[key] === 'string') {
        values[key] = '';
      } else if (typeof values[key] === 'object') {
        values[key] = null;
      }
      return false;
    });
    values.ancillaryTypeUniqueId = formValues.ancillaryTypeUniqueId;
    reset(values);
    onSubmit(values);
  };

  const getStateOptions = () => {
    if (!hasPermissions([EPermission.DISABLE_ANCILLARY])) {
      return mapDictionaryToOptionProp(state).filter(
        (item) => item.value !== EAncillaryState.DISABLED,
      );
    }
    return mapDictionaryToOptionProp(state);
  };

  return (
    <SearchWrapper padding={searchAncillaryFromLE && !showAdvanced ? '0' : undefined}>
      <form onSubmit={handleSubmit(submitForm)}>
        {!searchAncillaryFromLE && (
          <Grid
            container
            sx={{ mb: { xs: 2, md: 0 }, justifyContent: 'space-between', gap: 1 }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <StyledPresetSelect
                options={
                  presets
                    ? preparePresetsList(
                        presets,
                        'ancillaries.accordion.selectFilterPreset.label',
                      )
                    : preparePresetsList([], 'ancillaries.accordion.selectFilterPreset.label')
                }
                name="preset"
                control={control}
                errors={errors}
                setValue={(name: string, value: string) => {
                  setValue('preset', value);
                  handleResetField();
                }}
                withValidation={false}
                onSelect={(val) => {
                  if (!val) {
                    handleResetField();
                  } else {
                    setPresetFilters(val, false, true);
                  }
                }}
                withLabel={false}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant={EButtonVariants.outlined}
                  label={t('ancillaries.accordion.advancedFilters.button')}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  fullWidth={!isDesktop}
                  size={EButtonSizes.small}
                />
              </Box>
            </Grid>
          </Grid>
        )}
        {showAdvanced && (
          <StyledAdvancedSearch container columnSpacing={2} columns={10}>
            <Grid size={{ xs: 12 }}>
              <StyledSubtitle size={{ xs: 3 }}>
                <Typography variant="subtitle2">
                  {t('ancillaries.accordion.subtilte.label')}
                </Typography>
              </StyledSubtitle>
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(artComponentType)}
                name="ancillaryTypeUniqueId"
                label={t(ESearchFormLabels.ancillaryTypeUniqueId)}
                setValue={(name: string, value: string) => {
                  setValue('ancillaryTypeUniqueId', value);
                  if (!value) {
                    componentTypeChange(null);
                    handleResetField();
                  }
                }}
                canBeEmpty
                control={control}
                errors={errors}
                onSelect={clearAfterComponentTypeChange}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="fireCycleId"
                label={t(ESearchFormLabels.fireCycleId)}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="marking"
                label={t(ESearchFormLabels.marking)}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="serialNumber"
                label={t(ESearchFormLabels.serialNumber)}
                control={control}
                errors={errors}
                disabled={checkDisabled('serialNumber')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="exhibitNumber"
                label={t(ESearchFormLabels.exhibitNumber)}
                control={control}
                errors={errors}
                disabled={checkDisabled('exhibitNumber')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                name="manufacturersUniqueIds"
                options={mapDictionaryToOptionProp(artComponentManufacturer)}
                label={t(ESearchFormLabels.manufacturersUniqueIds)}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={checkDisabled('manufacturersUniqueIds')}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="productName"
                label={t(ESearchFormLabels.productName)}
                control={control}
                errors={errors}
                disabled={checkDisabled('productName')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(artComponentCalibre)}
                name="calibresUniqueIds"
                label={t(ESearchFormLabels.calibresUniqueIds)}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={checkDisabled('calibresUniqueIds')}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLengthNumber
                name="length"
                label={t(ESearchFormLabels.length)}
                control={control}
                errors={errors}
                disabled={checkDisabled('length')}
                setValue={setValue}
                type={EFormInputTypes.number}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(artComposition)}
                name="compositionsUniqueIds"
                label={t(ESearchFormLabels.compositionsUniqueIds)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('compositionsUniqueIds')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(artComponentCapacity)}
                name="componentCapacitiesUniqueIds"
                label={t(ESearchFormLabels.componentCapacitiesUniqueIds)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('componentCapacitiesUniqueIds')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(artComponentProofHouse)}
                name="proofHousesUniqueIds"
                label={t(ESearchFormLabels.proofHousesUniqueIds)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('proofHousesUniqueIds')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={getStateOptions()}
                name="statesUniqueIds"
                label={t(ESearchFormLabels.statesUniqueIds)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('statesUniqueIds')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(legality)}
                name="legalityUniqueId"
                label={t(ESearchFormLabels.legalityUniqueId)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('legalityUniqueId')}
                setValue={setValue}
              />
            </Grid>

            {customFields.map((customField) => (
              <FormCustomField
                data={customField}
                setValue={setValue}
                control={control}
                errors={errors}
                key={customField.uniqueId}
                wrapper={customFieldWrapper}
                isSearch={true}
              />
            ))}

            {new Array(4 - customFields.length >= 0 ? 4 - customFields.length : 0)
              .fill('')
              .map((index) => (
                <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }} key={index} />
              ))}

            {hasPermissions([EPermission.VIEW_LE]) && (
              <Grid size={{ xs: 12 }} container columns={10} columnSpacing={2}>
                <Grid size={{ xs: 10, sm: 5, lg: 7 }}>
                  <StyledFormSelect
                    name="ownerUniqueId"
                    label={t(ESearchFormLabels.owner)}
                    control={control}
                    errors={errors}
                    options={ownerOptions}
                    disabled={checkDisabled('ownerUniqueId')}
                    setValue={setValue}
                  />
                </Grid>
                <Grid size={{ xs: 10, sm: 5, lg: 3 }}>
                  <Button
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.small}
                    label={t('ancillaries.accordion.selectLegalEntity.button')}
                    fullWidth
                    sx={{
                      mt: {
                        sm: 3.5,
                      },
                    }}
                    onClick={() =>
                      navigate(ERouteLinks.SelectLegalEntity, {
                        state: {
                          legalityUniqueId: routerState?.legalityUniqueId,
                          type: ESelectLE.owner,
                          form: getValues(),
                          isFromMap: isFromMap || routerState?.isFromMap,
                          module: EArtAncillaryTypeNameLowerCase.COMPONENTS,
                          options: { keeperOptions, ownerOptions },
                          returnPath,
                          context: 'searchAncillaries',
                          ...(addAncillaryToGroupContext
                            ? {
                                ancillaryOption: EArtAncillaryType.COMPONENTS,
                                selectedAncillaries,
                              }
                            : undefined),
                          componentsUniqueIds,
                          fromSelectLE: true,
                          query: window.location.search,
                          breadcrumbs: [
                            {
                              label: t('module.ancillaries.name'),
                              route: ERouteLinks.Ancillaries,
                            },
                            t('ancillaries.search.selectLegal.label'),
                          ],
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            )}
            {!addAncillaryToGroupContext && hasPermissions([EPermission.VIEW_LE]) && (
              <Grid size={{ xs: 12 }} container columns={10} columnSpacing={2}>
                <Grid size={{ xs: 10, sm: 5, lg: 7 }}>
                  <StyledFormSelect
                    name="keeperUniqueId"
                    label={t(ESearchFormLabels.keeper)}
                    control={control}
                    errors={errors}
                    options={keeperOptions}
                    disabled
                    setValue={setValue}
                  />
                </Grid>
                <Grid size={{ xs: 10, sm: 5, lg: 3 }}>
                  <Button
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.small}
                    label={t('ancillaries.accordion.selectLegalEntity.button')}
                    fullWidth
                    sx={{
                      mt: {
                        sm: 3.5,
                      },
                    }}
                    onClick={() =>
                      navigate(ERouteLinks.SelectLegalEntity, {
                        state: {
                          type: ESelectLE.keeper,
                          form: getValues(),
                          isFromMap: isFromMap || routerState?.isFromMap,
                          module: EArtAncillaryTypeNameLowerCase.COMPONENTS,
                          options: { keeperOptions, ownerOptions },
                          returnPath,
                          context: 'searchAncillaries',
                          componentsUniqueIds,
                          query: window.location.search,
                          fromSelectLE: true,
                          breadcrumbs: [
                            {
                              label: t('module.ancillaries.name'),
                              route: ERouteLinks.Ancillaries,
                            },
                            t('ancillaries.search.selectLegal.label'),
                          ],
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <StyledSubtitle size={{ xs: 3 }}>
                <Typography variant="subtitle2">
                  {t('ancillaries.accordion.firearmFilters.label')}
                </Typography>
              </StyledSubtitle>
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(
                  getFirearmTypesForComponent(firearmType, watchComponentTypeUniqueId),
                )}
                name="frtTypesUniqueIds"
                label={t(ESearchFormLabels.frtTypesUniqueIds)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('frtTypesUniqueIds')}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormAsyncMultiAutocomplete
                name="frtMakesUniqueIds"
                dictionaryType={EDictionaryTypes.Make}
                label={t(ESearchFormFirearmLabels.makesUniqueIds)}
                control={control}
                disabled={checkDisabled('frtMakesUniqueIds')}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormAsyncMultiAutocomplete
                name="frtModelsUniqueIds"
                dictionaryType={EDictionaryTypes.Model}
                label={t(ESearchFormFirearmLabels.modelsUniqueIds)}
                control={control}
                disabled={checkDisabled('frtModelsUniqueIds')}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormAsyncMultiAutocomplete
                name="frtManufacturersUniqueIds"
                dictionaryType={EDictionaryTypes.Manufacturer}
                label={t(ESearchFormFirearmLabels.manufacturersUniqueIds)}
                control={control}
                disabled={checkDisabled('frtManufacturersUniqueIds')}
                errors={errors}
              />
            </Grid>

            <Grid
              size={{ xs: 12 }}
              container
              columns={10}
              columnSpacing={2}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
                <Button
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  disabled={isSearchButtonDisabled}
                  label={t('ancillaries.accordion.search.button')}
                  type="submit"
                  id="ancillaries_components_search-button"
                  fullWidth
                  sx={{
                    mt: {
                      xs: 2,
                      sm: 3.5,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </StyledAdvancedSearch>
        )}
      </form>
      {showAdvanced && (
        <SelectedFilters
          name="components"
          values={values}
          excludes={[
            'preset',
            'uniqueIds',
            'ownerName',
            'keeperName',
            'perPage',
            'currentPage',
          ]}
          noRemovable={['uniqueIds']}
          labels={ESearchFormLabels}
          additionalSaveFilterValues={{
            owner: ownerOptions[0],
            keeper: keeperOptions[0],
            componentsUniqueIds,
          }}
          handleDelete={handleResetField}
          type={ESearchFilterKeys.component}
          updatedQueryPath={
            getNewQueryPath ? getNewQueryPath(prepareData(getValues())) : undefined
          }
          saveRoute={ERouteLinks.SaveAncillariesPreset.replace(
            ':type',
            ESearchFilterKeys.component,
          )}
          additionalPath={
            isFromMap || (routerState && routerState.isFromMap) ? '?fromMap=true' : ''
          }
          showSaveFilters={addAncillaryToGroupContext ? false : showSaveFilter}
          dictionaries={{
            ancillaryTypeUniqueId: prepareFilters(artComponentType, 'uniqueId', 'name'),
            manufacturersUniqueIds: prepareFilters(
              artComponentManufacturer,
              'uniqueId',
              'name',
            ),
            calibresUniqueIds: prepareFilters(artComponentCalibre, 'uniqueId', 'name'),
            compositionsUniqueIds: prepareFilters(artComposition, 'uniqueId', 'name'),
            componentCapacitiesUniqueIds: prepareFilters(
              artComponentCapacity,
              'uniqueId',
              'name',
            ),
            proofHousesUniqueIds: prepareFilters(artComponentProofHouse, 'uniqueId', 'name'),
            statesUniqueIds: prepareFilters(state, 'uniqueId', 'name'),
            legalityUniqueId: prepareFilters(legality, 'uniqueId', 'name'),
            frtMakesUniqueIds: prepareFilters(make, 'uniqueId', 'name'),
            frtModelsUniqueIds: prepareFilters(model, 'uniqueId', 'name'),
            frtTypesUniqueIds: prepareFilters(firearmType, 'uniqueId', 'name'),
            frtManufacturersUniqueIds: prepareFilters(manufacturer, 'uniqueId', 'name'),
            ownerUniqueId: prepareFilters(ownerOptions, 'value', 'label'),
            keeperUniqueId: prepareFilters(keeperOptions, 'value', 'label'),
          }}
          dictionariesToSave={['ownerUniqueId', 'keeperUniqueId']}
          customFields={customFields}
          saveRouterStateAfterClearAll={addAncillaryToGroupContext}
          saveQueryParams={addAncillaryToGroupContext ? ['type'] : undefined}
        />
      )}
    </SearchWrapper>
  );
};

export { ComponentsSearchForm };
